import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from "@chakra-ui/modal";
import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";
import PureDropDown from "components/pure-dropdown";
import {
  getIntegrationTypes,
  createIntegration,
  updateIntegration,
  getIntegration,
  testIntegrationConnectivity,
} from "../api";
import {
  Integration,
  IntegrationType,
} from "../types";
import { useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import toast from "react-hot-toast";
import { useTranslation } from "hooks/useTranslation";
import ConfigurationFields from "./ConfigurationFields";
import {
  convertConfigurationsFromApiFormat,
  convertConfigurationsToApiFormat,
} from "./utils/configFormatConverter";

interface IntegrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  integration: Integration | null;
}

const IntegrationDialog = ({
  isOpen,
  onClose,
  integration,
}: IntegrationDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("");
  const [configurations, setConfigurations] = useState<Record<string, any>>({});
  const [integrationTypes, setIntegrationTypes] = useState<IntegrationType[]>(
    []
  );
  const [selectedType, setSelectedType] = useState<IntegrationType | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const isEditMode = !!integration;

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Reset form state immediately when dialog opens or integration changes
      setName("");
      setDescription("");
      setType("");
      setConfigurations({});
      setFormErrors({});
      setError(null);
      
      getIntegrationTypes().then((types) => {
        setIntegrationTypes(types);
        if (integration) {
          getIntegration(integration.id)
            .then((data) => {
              setName(data.name);
              setDescription(data.description);
              setType(data.integration_id);
              
              // Find the integration type to check configuration types
              const integrationType = types.find((t) => t.id === data.integration_id);
              
              // Convert configurations from API format to internal format
              const convertedConfigs = convertConfigurationsFromApiFormat(
                data.user_inputs || {},
                integrationType?.configurations || []
              );
              
              setConfigurations(convertedConfigs);
            })
            .catch(() => {
              setError(t("integrations.failedToFetchDetails"));
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      });
    }
  }, [isOpen, integration, t]);

  useEffect(() => {
    if (type) {
      const selected = integrationTypes.find((t) => t.id === type);
      setSelectedType(selected || null);
    } else {
      setSelectedType(null);
    }
  }, [type, integrationTypes]);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) {
      errors.name = t("integrations.nameRequired");
    }
    if (!type) {
      errors.type = t("integrations.typeRequired");
    }
    selectedType?.configurations.forEach((config: any) => {
      if (config.is_required) {
        if (config.type === "array") {
          const arrayValue = configurations[config.name];
          if (!arrayValue || !Array.isArray(arrayValue) || arrayValue.length === 0) {
            errors[config.name] = t("integrations.configRequired", { name: config.name });
          }
        } else {
          if (!configurations[config.name]) {
            errors[config.name] = t("integrations.configRequired", { name: config.name });
          }
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForTest = () => {
    if (isEditMode) {
      setFormErrors({});
      return true;
    }

    const errors: Record<string, string> = {};
    if (!type) {
      errors.type = t("integrations.typeRequired");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    setLoading(true);
    setError(null);
    
    // Convert configurations to API format
    const apiFormatConfigs = convertConfigurationsToApiFormat(
      configurations,
      selectedType?.configurations || []
    );
    
    const payload = {
      name,
      description,
      type,
      user_inputs: apiFormatConfigs,
      integration_type_id: type,
    };

    const request = isEditMode
      ? updateIntegration(integration!.id, payload)
      : createIntegration(payload);

    request
      .then(() => {
        onClose();
      })
      .catch(() => {
        setError(t("integrations.errorOccurred"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTestConnectivity = () => {
    if (!validateForTest()) {
      return;
    }
    setTestLoading(true);

    // Convert configurations to API format
    const apiFormatConfigs = convertConfigurationsToApiFormat(
      configurations,
      selectedType?.configurations || []
    );

    const payload = {
      integration_type_id: type,
      configurations: apiFormatConfigs,
    };

    testIntegrationConnectivity(payload)
      .then((response) => {
        console.log("Connectivity Test Result:", response);
      })
      .catch(() => {
        toast.error(t("integrations.connectivityTestError"));
      })
      .finally(() => {
        setTestLoading(false);
      });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement={document.documentElement.dir === "rtl" ? "left" : "right"}
    >
      <div
        className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
            `}
      />
      <DrawerContent className="my-4 ml-0 mr-4 w-full max-w-lg rounded-2xl bg-white shadow-[-20px_17px_40px_4px_rgba(112,_144,_176,_0.18)] dark:bg-navy-800 dark:shadow-[-22px_32px_51px_4px_#0B1437]">
        <DrawerHeader px="28px" pt="24px" pb="0px">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {isEditMode ? t("integrations.editIntegration") : t("integrations.createNewIntegration")}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {isEditMode
                  ? t("integrations.updateDetailsDescription")
                  : t("integrations.fillDetailsDescription")}
              </p>
            </div>
          </div>
        </DrawerHeader>
        <DrawerCloseButton className="absolute right-[26px] top-[16px] h-4 w-4 text-gray-900 dark:text-white" />
        <DrawerBody px="28px" pt="16px" pb="24px" height="100%" overflow="hidden">
          <div className="space-y-4" style={{ height: 'calc(100% - 53px)', overflow: 'auto', paddingRight: '4px'}}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}
            <InputField
              id="name"
              label={t("common.name")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              placeholder={t("integrations.integrationNamePlaceholder")}
              disabled={loading}
              state={formErrors.name ? "error" : undefined}
            />
            {formErrors.name && (
              <p className="text-xs text-red-500">{formErrors.name}</p>
            )}
            <TextField
              id="description"
              label={t("common.description")}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
              placeholder={t("integrations.integrationDescriptionPlaceholder")}
              cols={30}
              rows={5}
              disabled={loading}
            />
            <div className="mt-4">
              <PureDropDown
                options={integrationTypes.map((it) => ({
                  value: it.id,
                  label: it.name,
                }))}
                value={type || undefined}
                onChange={(value) => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  setType(selectedValue);
                  setConfigurations({});
                  setFormErrors({});
                }}
                placeholder={t("integrations.selectType")}
                label={t("integrations.typeLabel")}
                disabled={loading}
                showClearButton={false}
                buttonClassName={
                  formErrors.type ? "border-red-500" : ""
                }
              />
              {formErrors.type && (
                <p className="mt-1 text-xs text-red-500">{formErrors.type}</p>
              )}
            </div>
            {selectedType?.configurations && selectedType.configurations.length > 0 ? (
              <ConfigurationFields
                configurations={selectedType.configurations}
                values={configurations}
                onChange={(name, value) => {
                  setConfigurations({
                    ...configurations,
                    [name]: value,
                  });
                }}
                errors={formErrors}
                disabled={loading}
              />
            ) : (
              <p className="text-sm text-gray-500">{t("integrations.noConfiguration")}</p>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleTestConnectivity}
              disabled={loading || testLoading}
              className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {testLoading ? (
                <span className="flex items-center gap-2">
                  <AiOutlineLoading className="animate-spin" />
                  {t("integrations.testing")}
                </span>
              ) : (
                t("integrations.testConnectivity")
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={loading || testLoading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <AiOutlineLoading className="animate-spin" />
                  {isEditMode ? t("common.saving") : t("common.creating")}
                </span>
              ) : isEditMode ? (
                t("common.saveChanges")
              ) : (
                t("common.create")
              )}
            </button>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default IntegrationDialog;
